import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isEntitled, SPECIALTY_MODULES, type SpecialtyModuleDescriptor } from '@emr/shared';

import { useSession } from '../auth/sessionContext.ts';
import { Panel } from '../components/primitives.tsx';

/**
 * The specialty-module catalog — direct user instruction, 2026-08-18: dermatology (`BACKLOG.md` M3)
 * is the first specialty module actually built, but the product concept is a *list* of specialty
 * modules, most of them not built yet at all (`@emr/shared`'s `SPECIALTY_MODULES`, catalogued from
 * `BACKLOG.md` M5-M22). This page shows the whole list — one real entry, eighteen visible "coming
 * soon" placeholders — rather than a single derm toggle standing alone.
 *
 * Three states per row, and each renders differently on purpose (same "the difference is visible in
 * the output type, not only a comment" reasoning `shell/navigation.ts` states for its own two-gate
 * split):
 *
 *   - `comingSoon` — inert. No link, a muted badge, nothing to click. `SPECIALTY_MODULES` guarantees
 *     these carry no `feature`, so there is nothing to check an entitlement against even if it
 *     wanted to.
 *   - `available` and entitled — a real link to the module's own page.
 *   - `available` and not entitled — the identical upgrade-shaped wording `GatedPages.tsx`'s
 *     `UpgradeRefusal` uses, not a link. A user who lacks a capability by role must never be told to
 *     upgrade, and the converse holds here too: an unlicensed feature must never look clickable.
 */

function ComingSoonBadge() {
  const { t } = useTranslation('specialtyModules');
  return (
    <span className="rounded-pill border border-line bg-canvas px-2 py-0.5 text-xs font-medium text-ink-muted">
      {t('comingSoon')}
    </span>
  );
}

function ModuleRow({ module }: { readonly module: SpecialtyModuleDescriptor }) {
  const { t } = useTranslation('specialtyModules');
  const session = useSession();

  if (module.status === 'comingSoon') {
    return (
      <li className="flex items-center justify-between gap-3 rounded-control border border-line p-3 text-sm text-ink-muted">
        <span>{module.label}</span>
        <ComingSoonBadge />
      </li>
    );
  }

  // `module.status === 'available'` — `specialtyModules.test.ts` guarantees `feature` is non-null
  // here, but the type itself is `Feature | null`, so this still branches rather than asserting.
  const entitled = module.feature !== null && isEntitled(session.tier, module.feature);

  if (!entitled) {
    return (
      <li className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
        <span className="text-ink">{module.label}</span>
        <span className="text-ink-muted">
          {t('availableNotEntitled')}{' '}
          <span className="text-xs">{t('availableNotEntitledHint')}</span>
        </span>
      </li>
    );
  }

  // The link target comes from the registry's own `route` field, not a hardcoded path — found in
  // review 2026-08-18: an earlier version hardcoded `/dermatology` here for every `available` row,
  // which worked only because dermatology was the sole available entry. The moment a second module
  // ships and flips to `available`, a hardcoded target would send both rows to the same chart,
  // silently routing a user into the wrong specialty's data. `specialtyModules.test.ts` guarantees
  // `route` is non-null (and unique across available entries) here, but the type itself is
  // `string | null`, so this still branches rather than asserting.
  if (module.route === null) {
    return (
      <li className="rounded-control border border-line p-3 text-sm text-ink-muted">
        {t('noPageConfigured', { label: module.label })}
      </li>
    );
  }

  return (
    <li className="rounded-control border border-line p-3 text-sm">
      <Link
        to={module.route}
        className="font-medium text-accent-text underline-offset-2 hover:underline"
      >
        {module.label}
      </Link>
    </li>
  );
}

export function SpecialtyModulesPage() {
  const { t } = useTranslation('specialtyModules');
  return (
    <div className="flex flex-col gap-4">
      <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
      <p className="m-0 text-sm text-ink-muted">{t('description')}</p>
      <Panel>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {SPECIALTY_MODULES.map((module) => (
            <ModuleRow key={module.id} module={module} />
          ))}
        </ul>
      </Panel>
    </div>
  );
}
